<template>
  <div class="qa-issue-panel">
    <a-tabs
      v-model:activeKey="activeTab"
      size="small"
      class="qa-issue-panel__tabs">
      <a-tab-pane
        v-if="showPendingTab"
        key="pending"
        :tab="`На подтверждении (${pendingIssues.length})`" />
      <a-tab-pane
        key="errors"
        :tab="`Замечания (${errorIssues.length})`" />
      <a-tab-pane
        key="recommendations"
        :tab="`Рекомендации (${recommendationIssues.length})`" />
    </a-tabs>

    <div
      v-if="mode === 'review' && canCreate && !readonly"
      class="qa-issue-panel__create">
      <a-button
        type="primary"
        block
        :class="{ 'qa-issue-panel__add--active': drawActive }"
        @click="onReviewAction">
        {{ reviewButtonText }}
      </a-button>
    </div>

    <div
      v-if="showFixProgress"
      class="qa-issue-panel__progress">
      Исправлено {{ fixProgress.resolved }} из {{ fixProgress.total }}
    </div>

    <a-empty
      v-if="!activeIssues.length && !resolvedIssues.length"
      :description="emptyDescription" />

    <a-empty
      v-else-if="!activeIssues.length"
      description="Все замечания обработаны" />

    <a-list
      v-if="activeIssues.length"
      :data-source="activeIssues"
      size="small">
      <template #renderItem="{ item }">
        <issue-card
          :issue="item"
          :mode="mode"
          :navigation-enabled="navigationEnabled"
          :readonly="readonly"
          :can-edit-rerender="canEditRerender"
          :deletable="canDelete(item)"
          @issue-hover="$emit('issue-hover', $event)"
          @goto="goToIssue"
          @delete="$emit('delete', $event)"
          @rerender-requirement="$emit('rerender-requirement', $event)"
          @resolve="onResolveRequested"
          @confirm="$emit('confirm', $event)"
          @reopen="openReopen" />
      </template>
    </a-list>

    <a-collapse
      v-if="resolvedIssues.length"
      class="qa-issue-panel__resolved"
      ghost>
      <a-collapse-panel
        key="resolved"
        :header="`Исправленные (${resolvedIssues.length})`">
        <a-list
          :data-source="resolvedIssues"
          size="small">
          <template #renderItem="{ item }">
            <issue-card
              :issue="item"
              :mode="mode"
              :navigation-enabled="navigationEnabled"
              :readonly="readonly"
              :can-edit-rerender="canEditRerender"
              :deletable="canDelete(item)"
              @issue-hover="$emit('issue-hover', $event)"
              @goto="goToIssue"
              @delete="$emit('delete', $event)"
              @rerender-requirement="$emit('rerender-requirement', $event)"
              @resolve="onResolveRequested"
              @confirm="$emit('confirm', $event)"
              @reopen="openReopen" />
          </template>
        </a-list>
      </a-collapse-panel>
    </a-collapse>

    <a-modal
      v-model:open="resolveModal.open"
      :title="resolveModal.resolution === 'fixed' ? 'Исправил' : 'Не получилось'"
      :width="720"
      :ok-button-props="{ disabled: resolveModal.resolution === 'couldnt_fix' && resolveCommentEmpty }"
      ok-text="Подтвердить"
      cancel-text="Отмена"
      @ok="confirmResolve">
      <p v-if="resolveModal.resolution === 'couldnt_fix'">
        Комментарий обязателен — опишите, почему не вышло.
      </p>
      <p v-else>
        Комментарий можно оставить, но необязательно.
      </p>
      <rich-text-editor
        v-model="resolveModal.comment"
        :upload-image="uploadImage"
        placeholder="Картинку можно вставить из буфера обмена" />
    </a-modal>

    <a-modal
      v-model:open="reopenModal.open"
      title="Не подтверждаю"
      :width="720"
      :ok-button-props="{ disabled: reopenCommentEmpty }"
      ok-text="Вернуть в работу"
      cancel-text="Отмена"
      @ok="confirmReopen">
      <p>Замечание вернётся исполнителю. Опишите, что осталось не так.</p>
      <rich-text-editor
        v-model="reopenModal.comment"
        :upload-image="uploadImage"
        placeholder="Картинку можно вставить из буфера обмена" />
    </a-modal>
  </div>
</template>

<script setup>
import { computed, reactive } from 'vue';
import IssueCard from '@/Components/CRM/QualityReview/IssueCard.vue';
import RichTextEditor from '@/Components/Common/RichTextEditor.vue';
import { isRichTextEmpty } from '@/helpers/richText';

const props = defineProps({
  canEditRerender: { type: Boolean, default: false },
  mode: { type: String, required: true },
  issues: { type: Array, default: () => [] },
  drawActive: { type: Boolean, default: false },
  drawEnabled: { type: Boolean, default: true },
  navigationEnabled: { type: Boolean, default: true },
  canCreate: { type: Boolean, default: true },
  // Наблюдатель (админ до перехода в редактирование) видит всё, но не может ничего менять.
  readonly: { type: Boolean, default: false },
  // Исправленные ниндзей замечания, которые проверяющий должен подтвердить или вернуть.
  pendingIssues: { type: Array, default: () => [] },
  // Загрузка картинок, вставленных в комментарий: (File) => Promise<string url>.
  uploadImage: { type: Function, default: null },
});

const emit = defineEmits([
  'toggle-draw', 'create', 'goto', 'issue-hover', 'delete', 'fixed', 'couldnt-fix', 'confirm', 'reopen',
  'rerender-requirement',
]);

const state = reactive({
  activeTab: null,
});

const reviewButtonText = computed(() => {
  if (!props.drawEnabled) return 'Добавить';
  return props.drawActive ? 'Рисование включено' : 'Добавить';
});

const pendingIssues = computed(() => props.pendingIssues ?? []);
const showPendingTab = computed(() => props.mode === 'review' && pendingIssues.value.length > 0);

const activeTab = computed({
  get() {
    if (props.mode === 'recommendations') return 'recommendations';
    if (state.activeTab === 'pending' && !showPendingTab.value) return 'errors';
    // Пока проверяющий сам ничего не выбрал, приоритет у того, что ждёт его решения.
    if (state.activeTab === null) return showPendingTab.value ? 'pending' : 'errors';
    return state.activeTab;
  },
  set(value) {
    state.activeTab = value;
  },
});

const RESOLVED_STATUSES = ['fixed', 'couldnt_fix', 'confirmed', 'rejected'];
const isResolved = (item) => RESOLVED_STATUSES.includes(item.status);

const errorIssues = computed(() => props.issues.filter((issue) => issue.kind !== 'recommendation'));
const recommendationIssues = computed(() => props.issues.filter((issue) => issue.kind === 'recommendation'));

const tabIssues = computed(() => {
  if (activeTab.value === 'pending') return pendingIssues.value;
  return activeTab.value === 'recommendations' ? recommendationIssues.value : errorIssues.value;
});

// В исправлении ниндзя видит только то, с чем ещё нужно что-то сделать,
// а закрытые замечания убираются в отдельный сворачиваемый блок.
const collapseResolved = computed(() => props.mode === 'fix' && activeTab.value === 'errors');
const activeIssues = computed(() => (
  collapseResolved.value ? tabIssues.value.filter((issue) => !isResolved(issue)) : tabIssues.value
));
const resolvedIssues = computed(() => (
  collapseResolved.value ? tabIssues.value.filter(isResolved) : []
));

const showFixProgress = computed(() => collapseResolved.value && tabIssues.value.length > 0);
const fixProgress = computed(() => {
  const blocking = tabIssues.value.filter((issue) => issue.kind === 'error');
  return { total: blocking.length, resolved: blocking.filter(isResolved).length };
});
const activeKind = computed(() => (activeTab.value === 'recommendations' ? 'recommendation' : 'error'));

const emptyDescription = computed(() => ({
  recommendations: 'Рекомендаций пока нет',
  pending: 'Нечего подтверждать',
}[activeTab.value] ?? 'Замечаний пока нет'));

const onReviewAction = () => {
  if (props.drawEnabled) {
    emit('toggle-draw', { active: !props.drawActive, kind: activeKind.value });
    return;
  }

  emit('create', { kind: activeKind.value });
};

const goToIssue = (issueId) => {
  if (props.navigationEnabled) emit('goto', issueId);
};

const canDelete = (item) => !props.readonly && (
  (props.mode === 'review' && activeTab.value !== 'pending')
  || (props.mode === 'recommendations' && item.kind === 'recommendation')
);

const resolveModal = reactive({
  open: false, issueId: null, resolution: 'fixed', comment: '',
});

const resolveCommentEmpty = computed(() => isRichTextEmpty(resolveModal.comment));

const onResolveRequested = ({ issue, resolution }) => {
  resolveModal.open = true;
  resolveModal.issueId = issue.id;
  resolveModal.resolution = resolution;
  resolveModal.comment = '';
};

const reopenModal = reactive({ open: false, issueId: null, comment: '' });

const reopenCommentEmpty = computed(() => isRichTextEmpty(reopenModal.comment));

const openReopen = (item) => {
  reopenModal.open = true;
  reopenModal.issueId = item.id;
  reopenModal.comment = '';
};

const confirmReopen = () => {
  if (reopenCommentEmpty.value) return;
  emit('reopen', { issueId: reopenModal.issueId, comment: reopenModal.comment });
  reopenModal.open = false;
};

const confirmResolve = () => {
  if (resolveModal.resolution === 'fixed') {
    emit('fixed', { issueId: resolveModal.issueId, comment: resolveModal.comment });
  } else {
    if (resolveCommentEmpty.value) return;
    emit('couldnt-fix', { issueId: resolveModal.issueId, comment: resolveModal.comment });
  }
  resolveModal.open = false;
};
</script>

<style scoped>
.qa-issue-panel__tabs {
  margin-bottom: 8px;
}
.qa-issue-panel__progress {
  margin-bottom: 8px;
  font-weight: 500;
}
.qa-issue-panel__resolved {
  margin-top: 8px;
}
.qa-issue-panel__resolved :deep(.ant-collapse-content-box) {
  padding-inline: 0;
}
.qa-issue-panel__create {
  margin-bottom: 12px;
}
.qa-issue-panel__add--active {
  opacity: 0.88;
}
</style>
